import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { TextInput, Button, Text, Switch, useTheme, HelperText, Snackbar, ActivityIndicator, Checkbox } from 'react-native-paper';
import { getAllCategories, createMenuItem, updateMenuItem } from '../../../api/adminAPI';
import { Picker } from '@react-native-picker/picker';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const MenuItemFormScreen = ({ route, navigation }) => {
  const theme = useTheme();
  const { item, categoryId } = route.params || {};
  const isEditMode = !!item;
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryIdState, setCategoryId] = useState(categoryId || '');
  const [imageUrl, setImageUrl] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  
  // UI state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [errors, setErrors] = useState({});
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Kategorileri yükle
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories();
        if (response.success) {
          setCategories(response.data || []);
        } else {
          console.error('Kategoriler yüklenirken hata:', response.message);
          setSnackbarMessage('Kategoriler yüklenemedi');
          setSnackbarVisible(true);
        }
      } catch (error) {
        console.error('Kategoriler yüklenirken hata:', error);
        setSnackbarMessage('Kategoriler yüklenemedi');
        setSnackbarVisible(true);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    fetchCategories();
  }, []);
  
  // Düzenleme modunda form alanlarını doldur
  useEffect(() => {
    if (isEditMode && item) {
      setName(item.name || '');
      setDescription(item.description || '');
      setPrice(item.price?.toString() || '');
      setCategoryId(item.category_id?.toString() || '');
      setImageUrl(item.image_url || '');
      setIsAvailable(item.is_available === undefined ? true : !!item.is_available);
      setIsFeatured(item.is_featured === undefined ? false : !!item.is_featured);
    }
  }, [isEditMode, item]);

  // Form doğrulama
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Ürün adı gereklidir';
    }
    
    if (!price.trim() || isNaN(parseFloat(price))) {
      newErrors.price = 'Geçerli bir fiyat giriniz';
    }
    
    if (!categoryIdState) {
      newErrors.categoryId = 'Kategori seçimi gereklidir';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Ürün kaydetme/güncelleme işlemi
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const menuItemData = {
        name,
        description,
        price: parseFloat(price),
        category_id: categoryIdState,
        image_url: imageUrl,
        is_available: isAvailable,
        is_featured: isFeatured
      };
      
      let response;
      
      if (isEditMode) {
        response = await updateMenuItem(item.id, menuItemData);
      } else {
        response = await createMenuItem(menuItemData);
      }
      
      if (response.success) {
        setSnackbarMessage(isEditMode ? 'Ürün güncellendi' : 'Yeni ürün oluşturuldu');
        setSnackbarVisible(true);
        
        // Kısa bir süre bekleyip listeye dön
        setTimeout(() => {
          navigation.goBack();
        }, 1500);
      } else {
        setSnackbarMessage(response.message || 'Bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error saving menu item:', error);
      setSnackbarMessage(error.message || 'Ürün kaydedilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
    }
  };

  if (loadingCategories) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#7A288A" />
        <Text style={styles.loadingText}>Kategoriler yükleniyor...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.formContainer}>
        {/* Ürün İsmi */}
        <TextInput
          label="Ürün Adı *"
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
          error={!!errors.name}
          disabled={loading}
          outlineColor="#7A288A"
          activeOutlineColor="#7A288A"
        />
        {errors.name && <HelperText type="error">{errors.name}</HelperText>}
        
        {/* Açıklama */}
        <TextInput
          label="Açıklama"
          value={description}
          onChangeText={setDescription}
          mode="outlined"
          multiline
          numberOfLines={3}
          style={styles.input}
          disabled={loading}
          outlineColor="#7A288A"
          activeOutlineColor="#7A288A"
        />
        
        {/* Fiyat */}
        <TextInput
          label="Fiyat *"
          value={price}
          onChangeText={setPrice}
          mode="outlined"
          style={styles.input}
          keyboardType="decimal-pad"
          error={!!errors.price}
          disabled={loading}
          outlineColor="#7A288A"
          activeOutlineColor="#7A288A"
          right={<TextInput.Affix text="₺" textStyle={{color: '#7A288A'}} />}
        />
        {errors.price && <HelperText type="error">{errors.price}</HelperText>}
        
        {/* Kategori Seçimi */}
        <Text style={styles.pickerLabel}>Kategori *</Text>
        <View style={[styles.pickerContainer, errors.categoryId ? styles.pickerError : null]}>
          <Picker
            selectedValue={categoryIdState}
            onValueChange={(itemValue) => setCategoryId(itemValue)}
            enabled={!loading}
            style={styles.picker}
          >
            <Picker.Item label="Kategori Seçin" value="" />
            {categories.map((category) => (
              <Picker.Item 
                key={category.id} 
                label={category.name} 
                value={category.id.toString()} 
              />
            ))}
          </Picker>
        </View>
        {errors.categoryId && <HelperText type="error">{errors.categoryId}</HelperText>}
        
        {/* Resim URL */}
        <TextInput
          label="Resim URL"
          value={imageUrl}
          onChangeText={setImageUrl}
          mode="outlined"
          style={styles.input}
          disabled={loading}
          placeholder="Resim için URL girin"
          outlineColor="rgb(125, 76, 236)"
          activeOutlineColor="rgb(125, 76, 236)"
        />
        
        {/* Resim Önizleme */}
        {imageUrl ? (
          <View style={styles.imagePreviewContainer}>
            <Image
              source={{ uri: imageUrl }}
              style={styles.imagePreview}
              onError={() => {
                setSnackbarMessage('Resim yüklenemedi. URL kontrolü yapın.');
                setSnackbarVisible(true);
              }}
            />
          </View>
        ) : (
          <View style={styles.imagePlaceholder}>
            <MaterialCommunityIcons name="food" size={48} color="rgb(125, 76, 236)" />
            <Text style={styles.placeholderText}>Resim Yok</Text>
          </View>
        )}
        
        {/* Satışta mı? */}
        <View style={styles.switchContainer}>
          <Text style={{color: 'rgb(125, 76, 236)', fontWeight: 'bold'}}>Satışta</Text>
          <Switch
            value={isAvailable}
            onValueChange={setIsAvailable}
            disabled={loading}
            color="rgb(125, 76, 236)"
          />
        </View>
        
        {/* Öne Çıkan Ürün mü? */}
        <View style={styles.switchContainer}>
          <Text style={{color: 'rgb(125, 76, 236)', fontWeight: 'bold'}}>Öne Çıkan Ürün</Text>
          <Switch
            value={isFeatured}
            onValueChange={setIsFeatured}
            disabled={loading}
            color="rgb(125, 76, 236)"
          />
        </View>
        
        {/* Kaydet Butonu */}
        <Button
          mode="contained"
          onPress={handleSave}
          style={[styles.button, {backgroundColor: 'rgb(125, 76, 236)'}]}
          loading={loading}
          disabled={loading}
          labelStyle={{color: 'white', fontWeight: 'bold'}}
        >
          <Text style={{color: 'white'}}>{isEditMode ? 'Güncelle' : 'Ürün Oluştur'}</Text>
        </Button>
        
        {/* İptal Butonu */}
        <Button
          mode="outlined"
          onPress={() => navigation.goBack()}
          style={[styles.button, {borderColor: 'rgb(125, 76, 236)'}]}
          disabled={loading}
          textColor="rgb(125, 76, 236)"
        >
          İptal
        </Button>
      </View>
      
      {/* Snackbar */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Kapat',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  formContainer: {
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 8,
    margin: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  pickerLabel: {
    fontSize: 16,
    marginBottom: 8,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: 'rgb(125, 76, 236)',
    borderRadius: 4,
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  pickerError: {
    borderColor: '#FF0000',
  },
  picker: {
    height: 50,
  },
  imagePreviewContainer: {
    alignItems: 'center',
    marginVertical: 16,
  },
  imagePreview: {
    width: 150,
    height: 150,
    borderRadius: 8,
    marginBottom: 8,
  },
  imagePlaceholder: {
    height: 150,
    width: '100%',
    backgroundColor: 'rgba(125, 76, 236, 0.1)',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 16,
    borderWidth: 1,
    borderColor: 'rgb(125, 76, 236)',
    borderStyle: 'dashed',
  },
  placeholderText: {
    marginTop: 8,
    color: '#757575',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(125, 76, 236, 0.1)',
    padding: 10,
    borderRadius: 8,
  },
  button: {
    marginVertical: 8,
  },
});

export default MenuItemFormScreen;
